import React from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@rocco/utils/cn';

export interface TileOverlayProps {
    /** Name of the default/latest board to display. */
    defaultBoardName?: string;
    /** Show a shimmer placeholder instead of the board name (e.g. while loading). */
    boardNameLoading?: boolean;
    /** Whether the item is already saved to a board. */
    isSaved?: boolean;
    /** Called when the Save/Saved button is clicked. */
    onSave?: (e: React.MouseEvent<HTMLButtonElement>) => void;
    /** Called when the board dropdown trigger is clicked. */
    onDropdownClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
    /** Ref forwarded to the board dropdown trigger button. */
    dropdownRef?: React.Ref<HTMLButtonElement>;
    /** Whether the overlay actions are disabled. */
    disabled?: boolean;
    /** Additional class name for the overlay container. */
    className?: string;
    /** Whether to hide the dropdown chevron (e.g. for logged-out users). */
    hideDropdownChevron?: boolean;
    /**
     * When true, forces the overlay to be visible regardless of hover state.
     * Useful for keeping the overlay showing while a popover (e.g. FollowSelector) is open.
     */
    forceVisible?: boolean;
    /**
     * Round the overlay's corners to match a rounded tile. Defaults to `true`;
     * pass `false` for square-cornered tiles (e.g. the 5.0 browse grid) so the
     * hover surface doesn't show a radius over a square image. (A `rounded-none`
     * class can't override the custom `rounded-tile` token via tailwind-merge.)
     */
    rounded?: boolean;
}

/**
 * Pinterest-style tile overlay with a board dropdown trigger (left)
 * and a quick-save button (right). Appears on hover over the tile image.
 */
export const TileOverlay = ({
    defaultBoardName,
    boardNameLoading = false,
    isSaved = false,
    onSave,
    onDropdownClick,
    dropdownRef,
    disabled = false,
    className,
    hideDropdownChevron = false,
    forceVisible = false,
    rounded = true,
}: TileOverlayProps) => {
    return (
        <div
            className={cn(
                'absolute inset-0 z-10 hidden p-[1.125rem] md:block',
                rounded && 'rounded-tile',
                forceVisible
                    ? 'opacity-100'
                    : 'opacity-0 transition-opacity duration-300 md:group-hover:opacity-100',
                className,
            )}
            style={{
                background:
                    'linear-gradient(0deg, rgba(0, 0, 0, 0.07) 0%, rgba(0, 0, 0, 0.07) 100%), linear-gradient(180deg, rgba(0, 0, 0, 0.20) -8.33%, rgba(102, 102, 102, 0.00) 72.99%)',
            }}
        >
            <div className="flex items-center justify-between gap-2">
                {/* Board dropdown trigger */}
                <button
                    ref={dropdownRef}
                    type="button"
                    disabled={disabled}
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onDropdownClick?.(e);
                    }}
                    className="flex min-w-0 cursor-pointer items-center gap-1 text-left disabled:opacity-50"
                >
                    <span className="truncate text-xl leading-relaxed font-semibold text-white">
                        {boardNameLoading ? (
                            <span className="inline-block h-5 w-28 animate-pulse rounded bg-white/30" />
                        ) : (
                            defaultBoardName
                        )}
                    </span>
                    {!hideDropdownChevron && !boardNameLoading && (
                        <ChevronDown className="size-4 shrink-0 text-white" />
                    )}
                </button>

                {/* Quick save button */}
                <button
                    type="button"
                    disabled={disabled}
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onSave?.(e);
                    }}
                    className={cn(
                        'shrink-0 cursor-pointer rounded-full px-2.5 py-1.25 text-sm font-bold text-white',
                        'transition-colors duration-200',
                        isSaved
                            ? 'bg-gray-700'
                            : 'bg-gray-900 hover:bg-gray-700',
                        'disabled:opacity-50',
                    )}
                >
                    {isSaved ? 'Saved' : 'Save'}
                </button>
            </div>
        </div>
    );
};
